import classnames from 'classnames';
import React from 'react';
import styled from 'styled-components';
import forwardIcon from '../assets/images/forward-15.svg';
import pauseIconDesktop from '../assets/images/pause-button.svg';
import playIconDesktop from '../assets/images/play-button.svg';
import rewindIcon from '../assets/images/rewind-15.svg';
import { useStoreActions, useStoreState } from '../store';
import { PlayerStatus } from '../typings';
import { theme } from '../utils/theme';
import { isSmartTV } from '../utils/tv';

const isTV = isSmartTV();

export const TopControls = () => {
  const status = useStoreState((state) => state.player.status);
  const controlsVisible = useStoreState((state) => state.player.controlsVisible);
  const resumeOverlayVisible = useStoreState((state) => state.player.resumeOverlayVisible);
  const castState = useStoreState((state) => state.chromecast.castState);
  const castSession = useStoreState((state) => state.chromecast.castSession);
  const actions = useStoreActions((actions) => actions);

  const rewindFastForwardDesktopSize = isTV ? '68px' : '44px';
  const playDesktopSize = isTV ? '134px' : '100px';

  function canShowControl(kind: 'playOrPause' | 'fastForward' | 'rewind') {
    let result = true;

    // Hide all controls in these cases
    if (status === PlayerStatus.Buffering || castState === 'CONNECTING' || resumeOverlayVisible) {
      result = false;
    }

    return result;
  }

  function handlePlayClick() {
    if (castSession) {
      actions.chromecast.playOrPause();
      return;
    }

    if (status === PlayerStatus.Playing) {
      actions.player.pause();
      actions.interactions.pause();
    } else {
      actions.player.play();
      actions.interactions.play();
    }
  }

  function handleRewindClick() {
    actions.player.rewind();
  }

  function handleFastForwardClick() {
    actions.player.fastForward();
  }

  return (
    <Container className={classnames('large-controls', { visible: controlsVisible })}>
      {canShowControl('rewind') && (
        <ControlButton
          data-testid="videoPlayer_rewind_button"
          icon={rewindIcon}
          mobileSize="30px"
          desktopSize={rewindFastForwardDesktopSize}
          onClick={handleRewindClick}
          onKeyDown={(e) => {
            e.preventDefault();
          }}
        />
      )}
      {canShowControl('playOrPause') && (
        <ControlButton
          data-testid="videoPlayer_playPause_button"
          icon={status === PlayerStatus.Playing ? pauseIconDesktop : playIconDesktop}
          mobileSize="71px"
          desktopSize={playDesktopSize}
          onClick={handlePlayClick}
          onKeyDown={(e) => {
            e.preventDefault();
          }}
        />
      )}
      {canShowControl('fastForward') && (
        <ControlButton
          data-testid="videoPlayer_fastForward_button"
          icon={forwardIcon}
          mobileSize="30px"
          desktopSize={rewindFastForwardDesktopSize}
          onClick={handleFastForwardClick}
          onKeyDown={(e) => {
            e.preventDefault();
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 72px;
  height: 100%;
  align-items: center;
  justify-content: center;

  @media ${theme.mobileMediaQuery} {
    grid-column-gap: 32px;
  }
`;

const ControlButton = styled.div<{
  mobileSize: string;
  desktopSize: string;
  icon: string;
}>`
  width: ${({ desktopSize }) => desktopSize};
  height: ${({ desktopSize }) => desktopSize};
  background: url(${({ icon: desktopIcon }) => desktopIcon}) no-repeat center / contain;
  cursor: pointer;

  @media ${theme.mobileMediaQuery} {
    width: ${({ mobileSize }) => mobileSize};
    height: ${({ mobileSize }) => mobileSize};
    background: url(${({ icon }) => icon}) no-repeat center / contain;
  }
`;
